import Alpine from 'alpinejs';
import { closeOpenDialogs, isDialogElement, isHtmlElement } from '../utils/dom';
import { captureFocus, restoreFocus } from '../utils/preserve-focus';
import { lazyAlpineRuntimeAttributes } from './lazy-alpine';
const DATA_MORPH_SYNC_STATE = 'data-morph-sync';
const DATA_MORPH_CHILDREN_ONLY = 'data-morph-children-only';
const DATA_MORPH_SKIP = 'data-morph-skip';
const DATA_MORPH_SKIP_CHILDREN = 'data-morph-skip-children';
const MORPH_SYNC_EVENT = 'morph:sync';
/**
 * The live element owns the runtime half of its lazy state.
 *
 * Server markup never carries `data-alpine-loading` or the initialized marker, so
 * patching attributes from it would strip them and re-lazify a component that is already
 * running — the queued init then re-runs directives over data the component still uses.
 * Copy the live values onto the incoming element before the morph compares attributes.
 *
 * The other lazy attributes are server-owned configuration (which roots are lazy, what
 * wakes them, what modules they need) and morph normally, so a section update can change
 * how a stable element initializes.
 */
function keepLazyRuntimeAttributes(el, toEl) {
    for (const name of lazyAlpineRuntimeAttributes) {
        const liveValue = el.getAttribute(name);
        if (liveValue === null) {
            toEl.removeAttribute(name);
        }
        else if (toEl.getAttribute(name) !== liveValue) {
            toEl.setAttribute(name, liveValue);
        }
    }
}
/**
 * Morph rootEl with html, merging default morph behavior (data-morph-sync, data-morph-skip, etc.)
 * with morphOptions. When rootEl is the element being updated, only its children are morphed.
 *
 * @param rootEl - The root element to morph
 * @param html - The new HTML string to morph into
 * @param morphOptions - Optional AlpineMorphConfig to merge with default behavior
 */
export function morph(rootEl, html, morphOptions) {
    const syncedElements = [];
    let flushScheduled = false;
    const scheduleFlush = () => {
        if (flushScheduled)
            return;
        flushScheduled = true;
        queueMicrotask(() => {
            for (const { el, detail } of syncedElements) {
                el.dispatchEvent(new CustomEvent(MORPH_SYNC_EVENT, { bubbles: true, detail }));
            }
            syncedElements.length = 0;
            flushScheduled = false;
        });
    };
    const pushSyncIfChanged = (el, previousValue, value) => {
        if (previousValue !== value) {
            syncedElements.push({ el, detail: { previousValue, value } });
            scheduleFlush();
        }
    };
    const syncInputValue = (newNode, oldNode) => {
        if (oldNode.type === 'file')
            return;
        const previousValue = oldNode.value;
        const value = newNode.value;
        if (value === null) {
            oldNode.value = '';
        }
        else {
            oldNode.value = value;
        }
        pushSyncIfChanged(oldNode, previousValue, value !== null && value !== void 0 ? value : '');
    };
    const syncCheckbox = (newNode, oldNode) => {
        const previousValue = oldNode.checked ? 'true' : 'false';
        const value = newNode.checked ? 'true' : 'false';
        oldNode.checked = newNode.checked;
        if (newNode.indeterminate !== oldNode.indeterminate) {
            oldNode.indeterminate = newNode.indeterminate;
        }
        pushSyncIfChanged(oldNode, previousValue, value);
    };
    const syncRadio = (newNode, oldNode) => {
        const previousValue = oldNode.checked ? oldNode.value : '';
        const value = newNode.checked ? newNode.value : '';
        oldNode.checked = newNode.checked;
        pushSyncIfChanged(oldNode, previousValue, value);
    };
    const syncTextareaValue = (newNode, oldNode) => {
        const previousValue = oldNode.value;
        const value = newNode.value;
        if (value !== oldNode.value) {
            oldNode.value = value;
        }
        const firstChild = oldNode.firstChild;
        if ((firstChild === null || firstChild === void 0 ? void 0 : firstChild.nodeType) === Node.TEXT_NODE) {
            if (value === '' && firstChild.nodeValue === oldNode.placeholder) {
                return;
            }
            firstChild.nodeValue = value;
        }
        pushSyncIfChanged(oldNode, previousValue, value);
    };
    const config = Object.assign(Object.assign({}, morphOptions), { updating(el, toEl, childrenOnly, skip, skipChildren) {
            var _a, _b, _c;
            if (el instanceof Element && toEl instanceof Element) {
                keepLazyRuntimeAttributes(el, toEl);
            }
            if (isHtmlElement(el) && isHtmlElement(toEl) && el === rootEl) {
                childrenOnly();
            }
            if (el.hasAttribute && el.hasAttribute(DATA_MORPH_CHILDREN_ONLY)) {
                childrenOnly();
            }
            if (el.hasAttribute && el.hasAttribute(DATA_MORPH_SKIP)) {
                skip();
            }
            if (el.hasAttribute && el.hasAttribute(DATA_MORPH_SKIP_CHILDREN)) {
                skipChildren();
            }
            if (((_a = el.hasAttribute) === null || _a === void 0 ? void 0 : _a.call(el, DATA_MORPH_SYNC_STATE)) && ((_b = toEl === null || toEl === void 0 ? void 0 : toEl.hasAttribute) === null || _b === void 0 ? void 0 : _b.call(toEl, DATA_MORPH_SYNC_STATE))) {
                if (el instanceof HTMLInputElement && toEl instanceof HTMLInputElement) {
                    syncInputValue(toEl, el);
                    if (el.type === 'checkbox') {
                        syncCheckbox(toEl, el);
                    }
                    else if (el.type === 'radio') {
                        syncRadio(toEl, el);
                    }
                }
                else if (el instanceof HTMLTextAreaElement && toEl instanceof HTMLTextAreaElement) {
                    syncTextareaValue(toEl, el);
                }
            }
            (_c = morphOptions === null || morphOptions === void 0 ? void 0 : morphOptions.updating) === null || _c === void 0 ? void 0 : _c.call(morphOptions, el, toEl, childrenOnly, skip, skipChildren);
        },
        updated(el, toEl) {
            var _a;
            (_a = morphOptions === null || morphOptions === void 0 ? void 0 : morphOptions.updated) === null || _a === void 0 ? void 0 : _a.call(morphOptions, el, toEl);
        },
        removing(el, skip) {
            var _a;
            let isSkipped = false;
            const skipRemoval = () => {
                isSkipped = true;
                skip();
            };
            if (el.hasAttribute && el.hasAttribute(DATA_MORPH_SKIP)) {
                skipRemoval();
            }
            (_a = morphOptions === null || morphOptions === void 0 ? void 0 : morphOptions.removing) === null || _a === void 0 ? void 0 : _a.call(morphOptions, el, skipRemoval);
            // Alpine's morph detaches the node before it destroys the Alpine tree on it, so this
            // is the last moment an open modal <dialog> is still in the document and can give up
            // the top layer. A skipped element stays put, so it keeps its dialogs.
            if (!isSkipped && isHtmlElement(el)) {
                closeOpenDialogs(el);
            }
        } });
    // Morphing can replace or remove the focused element, dropping focus to <body> —
    // capture it up front and restore it (or the closest equivalent) afterwards
    const focusSnapshot = captureFocus(rootEl);
    // Alpine's morph drops a keyed element with a bare remove() or replaceWith(), which never
    // reaches the removing() hook above. An open modal <dialog> detached that way holds the
    // top layer and leaves every tap on the page dead, so collect them and check afterwards.
    const openDialogs = Array.from(rootEl.querySelectorAll('dialog[open]'));
    Alpine.morph(rootEl, html, config);
    // close() takes the dialog out of the document's top layer whether or not the element is
    // still attached, so a stranded one gives the page back
    openDialogs.forEach((el) => {
        if (isDialogElement(el) && el.open && !el.isConnected)
            el.close();
    });
    restoreFocus(rootEl, focusSnapshot);
}
